import React, { useState } from "react";
import toast from "react-hot-toast";
import { generateCompanyQuestions } from "../services/jobService";
import { GenerateQuestionsPanel, EditableQuestionList } from "./QuestionGenControls";

/* ── AI Interview question source manager ──────────────────────────────────
   Same "Write manually | Generate with AI" tab bar as QuestionSourceManager
   (MCQ/Coding) — no Upload Excel/CSV tab, since interview questions aren't
   spreadsheet rows. The always-editable list below is EditableQuestionList,
   which already provides its own "+ Add question" affordance for the manual
   tab, exactly mirroring how QuestionSourceManager keeps its question list
   visible under every tab. Controlled component: the caller owns role/JD/
   difficulty/questions state, so it works identically whether questions are
   held locally (Create Job form) or persisted immediately (Manage Questions). ── */

export function InterviewQuestionSourceManager({
  companyName, role, onRoleChange, jdText, onJdTextChange, difficulty, onDifficultyChange,
  questions, onQuestionsChange
}) {
  const [tab, setTab] = useState("manual");
  const [experience, setExperience] = useState("");
  const [numQuestions, setNumQuestions] = useState(10);
  const [generating, setGenerating] = useState(false);

  const handleGenerate = async () => {
    if (!role.trim()) {
      toast.error("Target role is required to generate questions.");
      return;
    }
    setGenerating(true);
    try {
      const result = await generateCompanyQuestions({
        event_type: "INTERVIEW", role, company_name: companyName, experience,
        jd_text: jdText, difficulty, num_questions: numQuestions
      });
      if (!result.questions?.length) {
        toast.error("No questions were generated — try again.");
        return;
      }
      onQuestionsChange([...questions, ...result.questions]);
      toast.success(`${result.questions.length} question(s) added — review below`);
    } catch (err) {
      toast.error(err.message || "Unable to generate questions");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="qbm-source">
      <div className="qbm-tabs">
        <button type="button" className={`qbm-tab${tab === "manual" ? " active" : ""}`} onClick={() => setTab("manual")}>
          Write manually
        </button>
        <button type="button" className={`qbm-tab${tab === "ai" ? " active" : ""}`} onClick={() => setTab("ai")}>
          ✨ Generate with AI
        </button>
      </div>

      {tab === "ai" && (
        <GenerateQuestionsPanel
          eventType="INTERVIEW"
          role={role} onRoleChange={onRoleChange}
          experience={experience} onExperienceChange={setExperience}
          jdText={jdText} onJdTextChange={onJdTextChange}
          difficulty={difficulty} onDifficultyChange={onDifficultyChange}
          numQuestions={numQuestions} onNumQuestionsChange={setNumQuestions}
          onGenerate={handleGenerate}
          generating={generating}
        />
      )}

      <EditableQuestionList questions={questions} onChange={onQuestionsChange} />
    </div>
  );
}
